import React, { useState, useRef, useEffect } from "react";

const ImageWithLoader = ({ src, alt, style }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  return (
    <div style={{ position: "relative" }}>
      {isLoading && (
        <div style={{ 
          position: "absolute", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)" 
        }}>
          <div style={{ 
            width: "32px", 
            height: "32px", 
            border: "4px solid #e2e8f0", 
            borderTopColor: "#3b82f6", 
            borderRadius: "50%", 
            animation: "spin 1s linear infinite" 
          }} />
        </div>
      )}
      <img 
        src={src} 
        alt={alt} 
        style={style} 
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const ImageModal = ({ images = [], selectedImageIndex = 0, onClose, onDelete, title }) => {
  const [currentIndex, setCurrentIndex] = useState(selectedImageIndex);
  const modalContentRef = useRef(null);

  // Handle clicks outside the image content
  const handleOutsideClick = (e) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    // Add keydown event listener
    window.addEventListener("keydown", handleKeyDown);
    
    // Create a style element for animations
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up function
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.head.removeChild(styleElement);
    };
  }, [currentIndex, images.length]);

  // Navigate to the previous image
  const handlePrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  // Navigate to the next image
  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Delete current image
  const handleDeleteCurrentImage = () => {
    onDelete(images[currentIndex].id);
    if (images.length === 1) {
      onClose();
    } else {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? prevIndex - 1 : prevIndex
      );
    }
  };

  // Download current image
  const handleDownloadImage = () => {
    const currentImage = images[currentIndex];
    const imageUrl =
      currentImage.id || currentImage.url || currentImage.uri || currentImage;

    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if current image is a signature (base64 data)
  const isSignature = (image) => {
    const imageStr = typeof image === "string" ? image : "";
    return imageStr.startsWith("data:image");
  };

  // Get current image source
  const getCurrentImageSrc = () => {
    if (!images.length) return "";
    const currentImage = images[currentIndex];
    return (
      currentImage.id || currentImage.url || currentImage.uri || currentImage
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: "0",
        left: "0",
        width: "100%", // Changed from 100vw to 100%
        height: "100%", // Changed from 100vh to 100%
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        boxSizing: "border-box", // Added to ensure proper sizing
        padding: "0 60px", // Added padding to prevent elements from being cut off
      }}
      onClick={handleOutsideClick}
    >
      {/* Close button - made more visible */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "16px",
          right: "40px", // Adjusted to ensure visibility
          background: "rgba(0, 0, 0, 0.6)",
          border: "none",
          cursor: "pointer",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "8px 12px",
          borderRadius: "20px",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
          transition: "background-color 0.2s",
          zIndex: 1010, // Ensure it's above other elements
        }}
        title="Close (Esc)"
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)"}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)"}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span style={{ fontSize: "14px", fontWeight: "500" }}>Close</span>
      </button>

      {/* Title */}
      {title && (
        <div style={{
          position: "absolute",
          top: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          color: "white",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          padding: "8px 16px",
          borderRadius: "8px",
          textAlign: "center",
          maxWidth: "500px",
          wordBreak: "break-word",
        }}>
          <h3 style={{ fontSize: "18px", fontWeight: "500", margin: 0 }}>{title}</h3>
        </div>
      )}

      {/* Main content */}
      {images.length === 0 ? (
        <div style={{
          color: "white",
          fontSize: "20px",
          fontWeight: "bold",
        }}>
          No image uploaded
        </div>
      ) : (
        <div style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}>
          {/* Previous button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
              style={{
                position: "absolute",
                left: "40px", // Adjusted to match right button
                background: "rgba(0, 0, 0, 0.6)",
                border: "none",
                cursor: "pointer",
                color: "white",
                padding: "12px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
                transition: "background-color 0.2s",
                zIndex: 1005, // Ensure it's above other elements
              }}
              title="Previous image (←)"
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
          )}

          {/* Image container */}
          <div 
            ref={modalContentRef} 
            style={{
              position: "relative",
              textAlign: "center",
              maxWidth: "90vw",
              maxHeight: "85vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                overflow: "auto",
                backgroundColor: isSignature(getCurrentImageSrc()) ? "#ffffff" : "transparent",
                padding: isSignature(getCurrentImageSrc()) ? "20px" : "0",
                borderRadius: "8px",
              }}
            >
              <ImageWithLoader
                src={getCurrentImageSrc()}
                alt={`Image ${currentIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </div>
            
            {/* Image counter */}
            <div style={{
              color: "white",
              marginTop: "16px",
              fontSize: "18px",
              fontWeight: "bold",
            }}>
              {`${currentIndex + 1}/${images.length}`}
            </div>
            
            {/* Action buttons */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "24px",
              marginTop: "16px",
            }}>
              {onDelete && (
                <button 
                  onClick={handleDeleteCurrentImage}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "rgba(0, 0, 0, 0.6)",
                    border: "none",
                    cursor: "pointer",
                    color: "#f87171",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    transition: "color 0.2s",
                  }}
                  title="Delete image"
                  onMouseOver={(e) => e.currentTarget.style.color = "#fca5a5"}
                  onMouseOut={(e) => e.currentTarget.style.color = "#f87171"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>Delete</span>
                </button>
              )}
              <button 
                onClick={handleDownloadImage}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  background: "rgba(0, 0, 0, 0.6)",
                  border: "none",
                  cursor: "pointer",
                  color: "white",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  transition: "color 0.2s",
                }}
                title="Download image"
                onMouseOver={(e) => e.currentTarget.style.color = "#e5e5e5"}
                onMouseOut={(e) => e.currentTarget.style.color = "white"}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>Download</span>
              </button>
            </div>
          </div>

          {/* Next button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              style={{
                position: "absolute",
                right: "40px", // Adjusted to ensure visibility
                background: "rgba(0, 0, 0, 0.6)",
                border: "none",
                cursor: "pointer",
                color: "white",
                padding: "12px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
                transition: "background-color 0.2s",
                zIndex: 1005, // Ensure it's above other elements
              }}
              title="Next image (→)"
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageModal;